import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import {
  GET_PENDING_ORDERS,
  GET_ALL_ORDERS,
  APPROVE_ORDER,
  REJECT_ORDER,
  LINK_JOB_TO_ORDER_ITEM,
} from '../graphql/orders';
import AddJobModal from '../components/AddJobModal';

const ChevronIcon = ({ isOpen }) => (
  <svg
    className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
    fill="none" stroke="currentColor" viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
  </svg>
);

const OrderDetailRow = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">{label}</p>
    <p className="text-sm font-medium text-gray-900">{value || 'N/A'}</p>
  </div>
);

const JobStatusBadge = ({ item, onCreateJob }) => {
  if (item.jobId && item.job) {
    const isCompleted = item.job.isCompleted;
    return (
      <div className="flex items-center gap-2">
        <a
          href={`/dashboard/jobs/${item.job.id}`}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
            isCompleted ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
          }`}
          aria-label={`View job - ${isCompleted ? 'Completed' : item.job.status}`}
        >
          {isCompleted ? (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          {isCompleted ? 'Completed' : item.job.status}
        </a>
        {item.job.assignedTo && (
          <span className="text-xs text-gray-500">
            {item.job.assignedTo.name}
          </span>
        )}
      </div>
    );
  }

  if (item.product?.serviceTypeId) {
    return (
      <button
        onClick={onCreateJob}
        className="inline-flex items-center gap-1.5 text-sm bg-sky-600 hover:bg-sky-700 text-white px-3 py-1.5 rounded transition-colors"
        aria-label={`Create job for ${item.title}`}
        tabIndex={0}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
        </svg>
        Create Job
      </button>
    );
  }

  return <span className="text-xs text-gray-400">No job type set</span>;
};

function Orders() {
  const [expandedPending, setExpandedPending] = useState({});
  const [expandedCompleted, setExpandedCompleted] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showNotification, setShowNotification] = useState(false);
  const [notification, setNotification] = useState({ message: '', type: 'info' });
  const [isJobModalOpen, setIsJobModalOpen] = useState(false);
  const [jobModalContext, setJobModalContext] = useState(null);

  const {
    loading: pendingLoading,
    error: pendingError,
    data: pendingData,
    refetch: refetchPending,
  } = useQuery(GET_PENDING_ORDERS);

  const {
    loading: allLoading,
    error: allError,
    data: allData,
    refetch: refetchAll,
  } = useQuery(GET_ALL_ORDERS);

  const [approveOrder] = useMutation(APPROVE_ORDER, {
    onCompleted: () => {
      refetchPending();
      refetchAll();
      showNotificationMessage('Order approved successfully', 'success');
    },
    onError: (error) => {
      showNotificationMessage(`Error approving order: ${error.message}`, 'error');
    },
  });

  const [rejectOrder] = useMutation(REJECT_ORDER, {
    onCompleted: () => {
      refetchPending();
      refetchAll();
      showNotificationMessage('Order rejected successfully', 'success');
    },
    onError: (error) => {
      showNotificationMessage(`Error rejecting order: ${error.message}`, 'error');
    },
  });

  const [linkJobToOrderItem] = useMutation(LINK_JOB_TO_ORDER_ITEM, {
    onCompleted: () => {
      refetchAll();
      showNotificationMessage('Job created and linked to order item', 'success');
    },
    onError: (error) => {
      showNotificationMessage(`Error linking job: ${error.message}`, 'error');
    },
  });

  const togglePendingAccordion = (orderId) => {
    setExpandedPending(prev => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  const toggleCompletedAccordion = (orderId) => {
    setExpandedCompleted(prev => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  const handleCreateJob = (order, item) => {
    setJobModalContext({ order, item });
    setIsJobModalOpen(true);
  };

  const handleJobCreated = async (jobData) => {
    if (jobModalContext?.item?.id && jobData?.id) {
      await linkJobToOrderItem({
        variables: {
          orderItemId: jobModalContext.item.id,
          jobId: jobData.id,
        },
      });
    }
    setIsJobModalOpen(false);
    setJobModalContext(null);
  };

  const buildJobNotes = (order, item) => {
    const parts = [item.title];
    if (item.variation?.name) parts.push(item.variation.name);
    if (order.notes) parts.push(`Guest Notes: ${order.notes}`);
    return parts.join('\n');
  };

  const handleApproveOrder = (order) => {
    approveOrder({ variables: { id: order.id } });
  };

  const handleOpenRejectDialog = (order) => {
    setSelectedOrder(order);
    setOpenRejectDialog(true);
  };

  const handleCloseRejectDialog = () => {
    setOpenRejectDialog(false);
    setRejectionReason('');
  };

  const handleSubmitReject = () => {
    rejectOrder({
      variables: {
        id: selectedOrder.id,
        reason: rejectionReason,
      },
    });
    handleCloseRejectDialog();
  };

  const showNotificationMessage = (message, type) => {
    setNotification({ message, type });
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 6000);
  };

  const getItemSummary = (items) => items.map(item => item.title).join(', ');

  const getVariantSummary = (items) =>
    items.map(item => item.variation?.name || '—').join(', ');

  if (pendingLoading && allLoading) {
    return (
      <div className="p-6 w-full">
        <h1 className="text-2xl font-semibold mb-6">Orders Management</h1>
        <div className="flex justify-center items-center min-h-[50vh]">
          <div className="text-gray-600">Loading orders data...</div>
        </div>
      </div>
    );
  }

  if (pendingError || allError) {
    return (
      <div className="p-6 w-full">
        <h1 className="text-2xl font-semibold mb-6">Orders Management</h1>
        <div className="flex justify-center items-center min-h-[50vh]">
          <div className="text-red-600">
            Error loading orders: {pendingError?.message || allError?.message}
          </div>
        </div>
      </div>
    );
  }

  const pendingOrders = pendingData?.findPendingOrders || [];
  const completedOrders = (allData?.findAll || []).filter(
    order => order.status === 'PAID' || order.status === 'APPROVED'
  );

  return (
    <div className="p-2 flex flex-wrap">
      {/* Pending Orders Section */}
      <div className="p-6 w-full">
        <div className="bg-white rounded-lg shadow-sm">
          <div className="flex justify-between items-center p-4 border-b">
            <h2 className="text-lg font-semibold">Pending Upsell Orders</h2>
            <span className="text-sm text-gray-500">{pendingOrders.length} pending</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="p-4 w-10"></th>
                  <th className="p-4 text-sm font-medium text-gray-600">Item Name</th>
                  <th className="p-4 text-sm font-medium text-gray-600">Variant</th>
                  <th className="p-4 text-sm font-medium text-gray-600">Request Date</th>
                  <th className="p-4 text-sm font-medium text-gray-600">Property</th>
                  <th className="p-4 text-sm font-medium text-gray-600">Total</th>
                  <th className="p-4 text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {pendingOrders.length > 0 ? (
                  pendingOrders.map(order => {
                    const isExpanded = expandedPending[order.id];
                    return (
                      <React.Fragment key={order.id}>
                        <tr
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => togglePendingAccordion(order.id)}
                        >
                          <td className="p-4">
                            <ChevronIcon isOpen={isExpanded} />
                          </td>
                          <td className="p-4 text-sm font-medium">{getItemSummary(order.items)}</td>
                          <td className="p-4 text-sm text-gray-600">{getVariantSummary(order.items)}</td>
                          <td className="p-4 text-sm">{format(new Date(order.createdAt), 'MMM dd, yyyy')}</td>
                          <td className="p-4 text-sm">{order.property?.name || 'N/A'}</td>
                          <td className="p-4 text-sm font-medium">${order.total.toFixed(2)}</td>
                          <td className="p-4" onClick={(e) => e.stopPropagation()}>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleApproveOrder(order)}
                                className="text-sm bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded"
                                aria-label="Approve order"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => handleOpenRejectDialog(order)}
                                className="text-sm bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded"
                                aria-label="Deny order"
                              >
                                Deny
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Expanded Details */}
                        {isExpanded && (
                          <tr>
                            <td colSpan="7" className="bg-gray-50 px-8 py-5">
                              <div className="space-y-4">
                                {/* Reservation Details */}
                                {order.reservation && (
                                  <div>
                                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Reservation Details</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-white rounded-lg border border-gray-200 p-4">
                                      <OrderDetailRow label="Confirmation #" value={order.reservation.streamline_reservation_id} />
                                      <OrderDetailRow label="Guest Name" value={order.reservation.guest_name || order.billingName} />
                                      <OrderDetailRow label="Email" value={order.reservation.email || order.billingEmail} />
                                      <OrderDetailRow label="Phone" value={order.reservation.mobile_phone || order.billingPhone} />
                                      <OrderDetailRow
                                        label="Check-in"
                                        value={order.reservation.check_in ? format(new Date(order.reservation.check_in), 'MMM dd, yyyy') : null}
                                      />
                                      <OrderDetailRow
                                        label="Check-out"
                                        value={order.reservation.check_out ? format(new Date(order.reservation.check_out), 'MMM dd, yyyy') : null}
                                      />
                                      <OrderDetailRow label="Property" value={order.property?.name} />
                                    </div>
                                  </div>
                                )}

                                {!order.reservation && (
                                  <div>
                                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Guest Details</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-white rounded-lg border border-gray-200 p-4">
                                      <OrderDetailRow label="Guest Name" value={order.billingName} />
                                      <OrderDetailRow label="Email" value={order.billingEmail} />
                                      <OrderDetailRow label="Phone" value={order.billingPhone} />
                                    </div>
                                  </div>
                                )}

                                {/* Order Items */}
                                <div>
                                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Order Items</h4>
                                  <div className="bg-white rounded-lg border border-gray-200 divide-y">
                                    {order.items.map(item => (
                                      <div key={item.id} className="flex items-center justify-between p-3">
                                        <div>
                                          <span className="text-sm font-medium text-gray-900">{item.title}</span>
                                          {item.variation?.name && (
                                            <span className="text-sm text-gray-500 ml-2">— {item.variation.name}</span>
                                          )}
                                          {item.quantity > 1 && (
                                            <span className="text-xs text-gray-400 ml-2">x{item.quantity}</span>
                                          )}
                                        </div>
                                        <span className="text-sm font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {/* Notes */}
                                {order.notes && (
                                  <div>
                                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Guest Notes</h4>
                                    <p className="text-sm text-gray-700 bg-white rounded-lg border border-gray-200 p-3">{order.notes}</p>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="p-4 text-center text-gray-500">
                      No pending orders found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Completed Orders Section */}
      <div className="p-6 w-full">
        <div className="bg-white rounded-lg shadow-sm">
          <div className="flex justify-between items-center p-4 border-b">
            <h2 className="text-lg font-semibold">Completed Orders</h2>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">{completedOrders.length} orders</span>
              <Link
                to="/dashboard/orders/history"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                View All Order History
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="p-4 w-10"></th>
                  <th className="p-4 text-sm font-medium text-gray-600">Item Name</th>
                  <th className="p-4 text-sm font-medium text-gray-600">Variant</th>
                  <th className="p-4 text-sm font-medium text-gray-600">Request Date</th>
                  <th className="p-4 text-sm font-medium text-gray-600">Property</th>
                  <th className="p-4 text-sm font-medium text-gray-600">Total</th>
                  <th className="p-4 text-sm font-medium text-gray-600">Payment</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {completedOrders.length > 0 ? (
                  completedOrders.map(order => {
                    const isExpanded = expandedCompleted[order.id];
                    return (
                      <React.Fragment key={order.id}>
                        <tr
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => toggleCompletedAccordion(order.id)}
                        >
                          <td className="p-4">
                            <ChevronIcon isOpen={isExpanded} />
                          </td>
                          <td className="p-4 text-sm font-medium">{getItemSummary(order.items)}</td>
                          <td className="p-4 text-sm text-gray-600">{getVariantSummary(order.items)}</td>
                          <td className="p-4 text-sm">{format(new Date(order.createdAt), 'MMM dd, yyyy')}</td>
                          <td className="p-4 text-sm">{order.property?.name || 'N/A'}</td>
                          <td className="p-4 text-sm font-medium">${order.total.toFixed(2)}</td>
                          <td className="p-4">
                            {order.status === 'PAID' ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                                Paid
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-700">
                                Approved
                              </span>
                            )}
                          </td>
                        </tr>

                        {/* Expanded Details */}
                        {isExpanded && (
                          <tr>
                            <td colSpan="7" className="bg-gray-50 px-8 py-5">
                              <div className="space-y-4">
                                {/* Reservation Details */}
                                {order.reservation && (
                                  <div>
                                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Reservation Details</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-white rounded-lg border border-gray-200 p-4">
                                      <OrderDetailRow label="Confirmation #" value={order.reservation.streamline_reservation_id} />
                                      <OrderDetailRow label="Guest Name" value={order.reservation.guest_name || order.billingName} />
                                      <OrderDetailRow label="Email" value={order.reservation.email || order.billingEmail} />
                                      <OrderDetailRow label="Phone" value={order.reservation.mobile_phone || order.billingPhone} />
                                      <OrderDetailRow
                                        label="Check-in"
                                        value={order.reservation.check_in ? format(new Date(order.reservation.check_in), 'MMM dd, yyyy') : null}
                                      />
                                      <OrderDetailRow
                                        label="Check-out"
                                        value={order.reservation.check_out ? format(new Date(order.reservation.check_out), 'MMM dd, yyyy') : null}
                                      />
                                    </div>
                                  </div>
                                )}

                                {!order.reservation && (
                                  <div>
                                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Guest Details</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-white rounded-lg border border-gray-200 p-4">
                                      <OrderDetailRow label="Guest Name" value={order.billingName} />
                                      <OrderDetailRow label="Email" value={order.billingEmail} />
                                      <OrderDetailRow label="Phone" value={order.billingPhone} />
                                    </div>
                                  </div>
                                )}

                                {/* Payment Info */}
                                <div>
                                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Payment</h4>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-white rounded-lg border border-gray-200 p-4">
                                    <OrderDetailRow label="Total" value={`$${order.total.toFixed(2)}`} />
                                    <OrderDetailRow
                                      label="Status"
                                      value={order.status === 'PAID' ? 'Paid via Stripe' : 'Approved (no payment)'}
                                    />
                                    <OrderDetailRow
                                      label="Paid At"
                                      value={order.paidAt ? format(new Date(order.paidAt), 'MMM dd, yyyy h:mm a') : null}
                                    />
                                    <OrderDetailRow
                                      label="Approved At"
                                      value={order.approvedAt ? format(new Date(order.approvedAt), 'MMM dd, yyyy h:mm a') : null}
                                    />
                                  </div>
                                </div>

                                {/* Order Items with Job Status */}
                                <div>
                                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Order Items & Jobs</h4>
                                  <div className="bg-white rounded-lg border border-gray-200 divide-y">
                                    {order.items.map(item => (
                                      <div key={item.id} className="flex items-center justify-between p-4">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-gray-900">{item.title}</span>
                                            {item.quantity > 1 && (
                                              <span className="text-xs text-gray-400">x{item.quantity}</span>
                                            )}
                                            <span className="text-sm text-gray-500">${(item.price * item.quantity).toFixed(2)}</span>
                                          </div>
                                          {item.variation?.name && (
                                            <p className="text-sm text-gray-500 mt-0.5">{item.variation.name}</p>
                                          )}
                                        </div>
                                        <div onClick={(e) => e.stopPropagation()}>
                                          <JobStatusBadge
                                            item={item}
                                            onCreateJob={() => handleCreateJob(order, item)}
                                          />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {/* Notes */}
                                {order.notes && (
                                  <div>
                                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Guest Notes</h4>
                                    <p className="text-sm text-gray-700 bg-white rounded-lg border border-gray-200 p-3">{order.notes}</p>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="p-4 text-center text-gray-500">
                      No completed orders found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="p-4 border-t">
            <span className="text-sm text-gray-600">Showing recent orders</span>
          </div>
        </div>
      </div>

      {/* Reject Order Dialog */}
      {openRejectDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium mb-4">Reject Order</h3>
            <p className="text-gray-500 mb-4">
              Please provide a reason for rejecting this order. This information will be shared with the customer.
            </p>
            <textarea
              className="w-full p-2 border rounded-lg focus:ring-1 focus:ring-sky-500 focus:border-sky-500 mb-4"
              rows="3"
              placeholder="Rejection reason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            ></textarea>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                onClick={handleCloseRejectDialog}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                onClick={handleSubmitReject}
              >
                Reject Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Job Modal */}
      {jobModalContext && (
        <AddJobModal
          isOpen={isJobModalOpen}
          onClose={() => {
            setIsJobModalOpen(false);
            setJobModalContext(null);
          }}
          onCreateJob={handleJobCreated}
          initialPropertyId={jobModalContext.order.property?.id}
          initialProperty={jobModalContext.order.property}
          initialServiceTypeId={jobModalContext.item.product?.serviceTypeId}
          initialNotes={buildJobNotes(jobModalContext.order, jobModalContext.item)}
          initialScheduledDate={
            jobModalContext.order.reservation?.check_in
              ? format(new Date(jobModalContext.order.reservation.check_in), 'yyyy-MM-dd')
              : undefined
          }
        />
      )}

      {/* Notification */}
      {showNotification && (
        <div className="fixed bottom-4 right-4 z-50">
          <div className={`p-4 rounded-lg shadow-lg max-w-md ${
            notification.type === 'success' ? 'bg-green-50 border border-green-200' :
            notification.type === 'error' ? 'bg-red-50 border border-red-200' :
            'bg-blue-50 border border-blue-200'
          }`}>
            <div className="flex">
              <div className="flex-shrink-0">
                {notification.type === 'success' && (
                  <svg className="h-5 w-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                {notification.type === 'error' && (
                  <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                {notification.type === 'info' && (
                  <svg className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${
                  notification.type === 'success' ? 'text-green-800' :
                  notification.type === 'error' ? 'text-red-800' :
                  'text-blue-800'
                }`}>
                  {notification.message}
                </p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setShowNotification(false)}
                  className={`inline-flex rounded-md p-1.5 ${
                    notification.type === 'success' ? 'text-green-500 hover:bg-green-100' :
                    notification.type === 'error' ? 'text-red-500 hover:bg-red-100' :
                    'text-blue-500 hover:bg-blue-100'
                  }`}
                  aria-label="Dismiss notification"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Orders;
